import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import invoicesService, { Invoice } from '../services/invoicesService';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const InvoiceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!id) {
        setError('Missing invoice ID');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await invoicesService.getInvoiceById(id);
        setInvoice(data);
      } catch (e) {
        console.error('Failed to load invoice', e);
        setError('Invoice not found or failed to load.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading invoice...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }}>
          Back to Invoices
        </Button>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }}>
          Back to Invoices
        </Button>
        <Typography>Invoice not found.</Typography>
      </Box>
    );
  }

  const status = (invoice.status || 'draft').toUpperCase();

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }}>
        Back to Invoices
      </Button>

      <Typography variant="h4" sx={{ mb: 2 }}>
        Invoice {invoice.invoiceNumber || '—'}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" color="textSecondary">Client</Typography>
              <Typography variant="h6">{invoice.client?.name || '—'}</Typography>
              <Typography variant="body2" color="textSecondary">{invoice.client?.company || ''}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">Project</Typography>
              <Typography variant="h6">{invoice.project || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">Issue Date</Typography>
              <Typography variant="h6">{invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString() : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">Due Date</Typography>
              <Typography variant="h6">{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '—'}</Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip label={status} />
            <Typography variant="h6">Total: {formatCurrency(invoice.totalAmount || 0)}</Typography>
            <Typography variant="body2" color="textSecondary">Tax: {formatCurrency(invoice.taxAmount || 0)}</Typography>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Items</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(invoice.items || []).length > 0 ? (
                (invoice.items || []).map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{it.description || '—'}</TableCell>
                    <TableCell align="right">{it.quantity ?? 0}</TableCell>
                    <TableCell align="right">{formatCurrency(it.rate ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(it.amount ?? 0)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="textSecondary">No items</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {invoice.notes && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="textSecondary">Notes</Typography>
              <Typography>{invoice.notes}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default InvoiceDetail;